import { useMemo, useState } from 'react';
import classNames from 'classnames';
import { NextLink } from '@common/NextLink/NextLink';
import qs from 'qs';
import { useTranslation } from 'next-i18next';
import type { Category } from '@models/data';
import { getLocale } from '@libs/constants';
import type { ParsedUrlQuery } from 'querystring';
import { useBreakpoints } from '@hooks/useBreakpoints';
import { BaseCollapsible } from '@product/Collapsible/BaseCollapsible';
import { Icon, IconColor, IconID } from '@common/Icon/Icon';
import { useRouter } from 'next/router';
import { CategoryHelper } from '@libs/category.helper';
import { useUrlParameterValues } from '@category/Filters/hooks/useUrlParameterValues';
import { IGUS_REWRITE } from '@libs/redirect.helper';
import { useFeatureFlag } from '@libs/abby/abby';

type CategoryMenuItemProps = {
  category: Category;
  filterQuery: ParsedUrlQuery;
  activeCategoryKey: string;
  layer?: number;
  parentCategories?: string[];
};

const CategoryMenuItem = ({
  category,
  filterQuery,
  activeCategoryKey,
  layer = 1,
  parentCategories = [],
}: CategoryMenuItemProps) => {
  const router = useRouter();
  const locale = getLocale(router);

  // some categories for the upcoming fair should only be visible via feature flag to allow releasing them on a specific point of time
  // TODO: after fair, remove the feature flag
  // ↑ This never happens anyway. Just use it for the new fair each year...
  const showNewFairCategories = useFeatureFlag('showNewFairCategories');
  const fairCategories = ['pulleys', 'suitable_for_Keyence', 'operating_parts'];

  const categoryIsActive = useMemo(
    () => CategoryHelper.isCategoryActive(category, activeCategoryKey),
    [category, activeCategoryKey]
  );

  const href = `${category.path}${Object.keys(filterQuery).length ? `?${qs.stringify(filterQuery)}` : ''}`;

  const shouldUseNextLink = useMemo(
    () => CategoryHelper.isEnabled([...parentCategories, category.key]),
    [category.key, parentCategories]
  );

  const isActive = category.key === activeCategoryKey;
  return (
    <li key={category.key} className="mb-4">
      {shouldUseNextLink ? (
        <NextLink href={href} className={classNames(isActive ? 'active' : '')} data-testid="next-category-link">
          {category.name}
        </NextLink>
      ) : (
        <a
          href={IGUS_REWRITE(category.aliases?.[locale] || category.aliases?.default || category.path, locale)}
          className={classNames(isActive ? 'active' : '')}
          data-testid="category-link"
        >
          {category.name}
        </a>
      )}
      {categoryIsActive && (
        <ul className="ml-8 mt-4">
          {category.children
            ?.filter(
              subCategory =>
                fairCategories.length === 0 || showNewFairCategories || !fairCategories.includes(subCategory.key)
            )
            .map(subCategory => (
              <CategoryMenuItem
                key={subCategory.key}
                category={subCategory}
                parentCategories={[...parentCategories, category.key]}
                filterQuery={filterQuery}
                activeCategoryKey={activeCategoryKey}
                layer={layer + 1}
              />
            ))}
        </ul>
      )}
    </li>
  );
};

export type CategoriesMenuProps = {
  categoryTree: Category;
  selectedCategory: Category;
};

export const CategoriesMenu = ({ categoryTree, selectedCategory }: CategoriesMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation(['category/categoriesMenu']);
  const { lg } = useBreakpoints();
  const filterQuery = useUrlParameterValues();

  const Categories = (
    <ul className="igus-text-styles mr-8">
      <CategoryMenuItem category={categoryTree} activeCategoryKey={selectedCategory.key} filterQuery={filterQuery} />
    </ul>
  );

  if (lg) {
    return (
      <>
        <div className="mb-8 pad2 text-3xl font-medium leading-2xl">{t('PRODUCT_OVERVIEW.CATEGORIES_TITLE')}</div>
        {Categories}
      </>
    );
  }

  return (
    <BaseCollapsible
      isOpen={isOpen}
      onOpen={() => setIsOpen(open => !open)}
      openIcon={<Icon id={IconID.ARROW_UP} size={6} primary={false} color={IconColor.ORANGE} />}
      closeIcon={<Icon id={IconID.ARROW_DOWN} size={6} primary={false} color={IconColor.ORANGE} />}
      title={<span className="text-3xl font-medium leading-2xl block">{t('PRODUCT_OVERVIEW.CATEGORIES_TITLE')}</span>}
    >
      <div className="mt-8">{Categories}</div>
    </BaseCollapsible>
  );
};
